import React, { useEffect, useMemo, useState } from 'react';
import Header from '@/components/ui/Header';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import Icon from '@/components/AppIcon';
import { useAuth } from '@/context/AuthContext';
import useTranslation from '@/hooks/useTranslation';
import {
  createSupportRequest,
  fetchSupportRequests,
} from '@/lib/api/support';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const formatDate = (value, fallback) => {
  const parsed = value ? new Date(value) : null;
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return fallback;
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const SupportPage = () => {
  const { t } = useTranslation(['header', 'support']);
  const { user } = useAuth();

  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submissionNotice, setSubmissionNotice] = useState(null);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    contactEmail: user?.email ?? '',
    contactName: user?.fullName ?? user?.name ?? '',
  });

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      contactEmail: prev.contactEmail || user?.email || '',
      contactName: prev.contactName || user?.fullName || user?.name || '',
    }));
  }, [user?.email, user?.fullName, user?.name]);

  useEffect(() => {
    let isMounted = true;
    setLoadingRequests(true);

    fetchSupportRequests()
      .then((data) => {
        if (isMounted) {
          setRequests(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (isMounted) {
          setRequests([]);
        }
      })
      .finally(() => {
        if (isMounted) {
          setLoadingRequests(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const statusLabels = useMemo(
    () => ({
      received: t('support.requests.status.received'),
      responded: t('support.requests.status.responded'),
    }),
    [t],
  );

  const validate = () => {
    const errors = {};
    if (!formData.subject?.trim()) {
      errors.subject = t('support.validation.subject');
    }

    if (!formData.message?.trim()) {
      errors.message = t('support.validation.message');
    }

    if (formData.contactEmail && !EMAIL_REGEX.test(formData.contactEmail)) {
      errors.contactEmail = t('support.validation.email');
    }

    return errors;
  };

  const resetForm = () => {
    setFormData((prev) => ({
      ...prev,
      subject: '',
      message: '',
    }));
    setFormErrors({});
  };

  const handleChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const errors = validate();
    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const created = await createSupportRequest({
        subject: formData.subject.trim(),
        message: formData.message.trim(),
        contactEmail: formData.contactEmail?.trim() || undefined,
        contactName: formData.contactName?.trim() || undefined,
      });

      setRequests((prev) => [created, ...prev]);
      setSubmissionNotice({
        id: created.id,
        subject: created.subject,
      });
      resetForm();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit support request', error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!submissionNotice) return undefined;
    const timeout = window.setTimeout(() => setSubmissionNotice(null), 6000);
    return () => window.clearTimeout(timeout);
  }, [submissionNotice]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="bg-muted/20 pt-20 md:pt-24">
        <div className="max-w-6xl mx-auto px-4 py-12 space-y-10">
          <header className="space-y-3 text-center">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary px-4 py-1 text-sm font-medium">
              <Icon name="Heart" size={16} />
              {t('support.page.badge')}
            </span>
            <div className="space-y-2">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                {t('support.page.title')}
              </h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                {t('support.page.description')}
              </p>
            </div>
          </header>

          {submissionNotice ? (
            <div className="rounded-2xl border border-success/40 bg-success/10 text-success px-6 py-4 flex items-start gap-3 shadow-sm">
              <Icon name="CheckCircle" size={20} />
              <div>
                <p className="font-semibold">{t('support.form.successTitle')}</p>
                <p className="text-sm">
                  {t('support.form.successBody')}{' '}
                  <span className="font-mono text-xs">{submissionNotice.id}</span>
                </p>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-8">
            <section className="rounded-3xl border border-border bg-card shadow-elevation-1 p-6 space-y-6">
              <div className="flex flex-col gap-1">
                <h2 className="text-xl font-semibold text-foreground">
                  {t('support.form.heading')}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t('support.form.subheading')}
                </p>
              </div>

              <form className="space-y-4" onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label={t('support.form.nameLabel')}
                    value={formData.contactName}
                    onChange={handleChange('contactName')}
                    placeholder={t('support.form.namePlaceholder')}
                  />
                  <Input
                    label={t('support.form.emailLabel')}
                    value={formData.contactEmail}
                    onChange={handleChange('contactEmail')}
                    placeholder={t('support.form.emailPlaceholder')}
                    error={formErrors.contactEmail}
                  />
                </div>

                <Input
                  label={t('support.form.subjectLabel')}
                  value={formData.subject}
                  onChange={handleChange('subject')}
                  placeholder={t('support.form.subjectPlaceholder')}
                  error={formErrors.subject}
                  required
                />

                <div className="space-y-2">
                  <label className={`text-sm font-medium ${formErrors.message ? 'text-destructive' : 'text-foreground'}`}>
                    {t('support.form.messageLabel')}
                    <span className="text-destructive ml-1">*</span>
                  </label>
                  <textarea
                    className={`min-h-[140px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${formErrors.message ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                    value={formData.message}
                    onChange={handleChange('message')}
                    placeholder={t('support.form.messagePlaceholder')}
                  />
                  {formErrors.message ? (
                    <p className="text-sm text-destructive">{formErrors.message}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('support.form.messageHelper')}
                    </p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground">
                    {t('support.form.commitment')}
                  </p>
                  <Button type="submit" className="sm:w-auto w-full" loading={submitting}>
                    {t('support.form.submitButton')}
                  </Button>
                </div>
              </form>
            </section>

            <aside className="space-y-6">
              <section className="rounded-3xl border border-border bg-card shadow-elevation-1 p-6 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <Icon name="Headset" size={18} />
                  </span>
                  <div className="space-y-1">
                    <h2 className="text-lg font-semibold text-foreground">
                      {t('support.contact.title')}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {t('support.contact.subtitle')}
                    </p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-foreground">
                  <li className="flex items-center gap-2">
                    <Icon name="Phone" size={16} className="text-primary" />
                    <span>{t('support.contact.phone')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Mail" size={16} className="text-primary" />
                    <span>{t('support.contact.email')}</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Icon name="Clock" size={16} className="text-primary" />
                    <span>{t('support.contact.hours')}</span>
                  </li>
                </ul>
                <div className="rounded-2xl bg-muted/50 border border-border px-4 py-3 space-y-1 text-sm text-muted-foreground">
                  <p className="font-medium text-foreground">
                    {t('support.contact.promise')}
                  </p>
                  <p>{t('support.contact.details')}</p>
                </div>
              </section>

              <section className="rounded-3xl border border-border bg-card shadow-elevation-1 overflow-hidden">
                <header className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      {t('support.requests.title')}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {t('support.requests.subtitle')}
                    </p>
                  </div>
                  <Icon name="History" size={18} className="text-muted-foreground" />
                </header>
                {loadingRequests ? (
                  <div className="px-6 py-5 text-sm text-muted-foreground">
                    {t('support.requests.loading')}
                  </div>
                ) : requests.length === 0 ? (
                  <div className="px-6 py-5 text-sm text-muted-foreground">
                    {t('support.requests.empty')}
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {requests.map((request) => (
                      <article key={request.id} className="px-6 py-4 space-y-2">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="font-medium text-foreground">{request.subject}</p>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">
                              {request.id}
                            </p>
                          </div>
                          <span className="inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1 bg-muted text-foreground border border-border">
                            {statusLabels[request.status] ?? request.status}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                          {request.message}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('support.requests.opened', {
                            date: formatDate(request.createdAt, t('support.requests.dateFallback')),
                          })}
                          {request.contactName
                            ? ` · ${t('support.requests.with', { contactName: request.contactName })}`
                            : ''}
                        </p>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SupportPage;
